/**
 * Hooks de plataformas conectadas (Self Social, dashboard de TOM, a medida).
 * Solo rol admin. El token y el secreto de firma vuelven una única vez.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api";

export const PLATFORM_PERMISSIONS = [
  { value: "read", label: "Ver", description: "Conversaciones, escalados y publicaciones" },
  { value: "reply", label: "Responder", description: "Contestar mensajes en nombre del cliente" },
  { value: "sandbox", label: "Probar bot", description: "Entorno de prueba (solo premium)" },
] as const;

export const PLATFORM_EVENTS = [
  { value: "escalation", label: "Escalados" },
  { value: "publication", label: "Publicaciones" },
  { value: "error", label: "Errores" },
] as const;

export interface Platform {
  slug: string;
  name: string;
  tokenPrefix: string;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  canCreateClients: boolean;
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformClientLink {
  platformSlug: string;
  clientSlug: string;
  permissions: string[];
  events: string[];
  createdByPlatform: boolean;
}

export interface PlatformEventRow {
  id: string;
  clientSlug: string;
  type: string;
  status: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

export interface PlatformDetail {
  platform: Platform;
  clients: PlatformClientLink[];
  events: PlatformEventRow[];
}

/** Lo que se muestra una sola vez después de crear o rotar. */
export interface PlatformSecrets {
  slug: string;
  token?: string;
  webhookSecret?: string;
}

export function usePlatforms() {
  return useQuery<Platform[]>({
    queryKey: ["admin", "platforms"],
    queryFn: async () => (await apiGet<{ items: Platform[] }>("/admin/platforms")).items,
  });
}

export function usePlatformDetail(slug: string | null) {
  return useQuery<PlatformDetail>({
    queryKey: ["admin", "platforms", slug],
    queryFn: () => apiGet<PlatformDetail>(`/admin/platforms/${slug}`),
    enabled: Boolean(slug),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["admin", "platforms"] });
}

export function useCreatePlatform() {
  const invalidate = useInvalidate();
  return useMutation<
    { platform: Platform; token: string; webhookSecret: string },
    Error,
    { slug: string; name: string; webhookUrl?: string | null; canCreateClients: boolean }
  >({
    mutationFn: (input) => apiPost("/admin/platforms", input),
    onSuccess: () => {
      invalidate();
      toast.success("Plataforma creada");
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useUpdatePlatform() {
  const invalidate = useInvalidate();
  return useMutation<
    { platform: Platform },
    Error,
    { slug: string; name?: string; webhookUrl?: string | null; canCreateClients?: boolean; active?: boolean }
  >({
    mutationFn: ({ slug, ...body }) => apiPatch(`/admin/platforms/${slug}`, body),
    onSuccess: () => {
      invalidate();
      toast.success("Plataforma actualizada");
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useRotatePlatformToken() {
  const invalidate = useInvalidate();
  return useMutation<{ platform: Platform; token: string }, Error, string>({
    mutationFn: (slug) => apiPost(`/admin/platforms/${slug}/rotate-token`),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message),
  });
}

export function useRotateWebhookSecret() {
  const invalidate = useInvalidate();
  return useMutation<{ platform: Platform; webhookSecret: string }, Error, string>({
    mutationFn: (slug) => apiPost(`/admin/platforms/${slug}/rotate-webhook-secret`),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message),
  });
}

export function useSetPlatformClientRules() {
  const invalidate = useInvalidate();
  return useMutation<
    unknown,
    Error,
    { slug: string; clientSlug: string; permissions: string[]; events: string[] }
  >({
    mutationFn: ({ slug, clientSlug, ...body }) =>
      apiPut(`/admin/platforms/${slug}/clients/${clientSlug}`, body),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message),
  });
}

export function useRemovePlatformClient() {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { slug: string; clientSlug: string }>({
    mutationFn: ({ slug, clientSlug }) => apiDelete(`/admin/platforms/${slug}/clients/${clientSlug}`),
    onSuccess: () => {
      invalidate();
      toast.success("Cliente quitado de la plataforma");
    },
    onError: (err) => toast.error(err.message),
  });
}
